
import { useState, useEffect } from 'react';
import type { EnhancedNotification } from '@/hooks/types/notificationTypes';

export const useBrowserNotification = () => {
  const [permission, setPermission] = useState<NotificationPermission>('default');

  useEffect(() => {
    if ('Notification' in window) {
      setPermission(Notification.permission);
    }
  }, []);

  const requestNotificationPermission = async () => {
    if (!('Notification' in window)) {
      // Browser does not support notifications
      return false;
    }

    const permission = await Notification.requestPermission();
    setPermission(permission);
    return permission === 'granted';
  };

  const showBrowserNotification = (notification: EnhancedNotification) => {
    if (permission !== 'granted' || document.visibilityState === 'visible') {
      return;
    }

    const browserNotification = new Notification(notification.title, {
      body: notification.message,
      icon: '/favicon.ico',
      badge: '/favicon.ico',
      tag: notification.id,
      requireInteraction: notification.priority === 'urgent',
    });

    // Auto close after 5 seconds for non-urgent notifications
    if (notification.priority !== 'urgent') {
      setTimeout(() => {
        browserNotification.close();
      }, 5000);
    }

    // Handle click
    browserNotification.onclick = () => {
      window.focus();
      if (notification.link) {
        window.open(notification.link, '_blank');
      }
      browserNotification.close();
    };
  };

  return {
    permission,
    requestNotificationPermission,
    showBrowserNotification,
  };
};
